import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { getBlogStatus, listTopics, listPosts, generateNow } from "@/lib/blog.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/expert-tips")({
  head: () => ({ meta: [{ title: "Admin · Expert Tips" }, { name: "robots", content: "noindex" }] }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) throw redirect({ to: "/login" });
  },
  component: AdminExpertTips,
});

function AdminExpertTips() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [signedIn, setSignedIn] = useState(true);
  const statusFn = useServerFn(getBlogStatus);
  const topicsFn = useServerFn(listTopics);
  const postsFn = useServerFn(listPosts);
  const genFn = useServerFn(generateNow);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) { setSignedIn(false); navigate({ to: "/login" }); }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const status = useQuery({ queryKey: ["admin-status"], queryFn: () => statusFn() });
  const topics = useQuery({ queryKey: ["admin-topics"], queryFn: () => topicsFn() });
  const posts = useQuery({ queryKey: ["admin-posts"], queryFn: () => postsFn() });

  const genMut = useMutation({
    mutationFn: (slot: "general" | "city") => genFn({ data: { slot } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-status"] });
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
    },
  });

  if (!signedIn) return null;

  const anyError = status.error || topics.error || posts.error;
  if (anyError) {
    return (
      <div className="p-8">
        <p className="text-destructive">
          {String((anyError as any)?.message || "")}. You may not have admin access yet – sign in with the admin email or contact support.
        </p>
        <Button className="mt-4" onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/login" }); }}>Sign out</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 p-6 lg:p-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/" className="text-xs text-muted-foreground hover:text-accent">← Site</Link>
            <h1 className="mt-1 text-3xl font-extrabold text-primary">Expert Tips Admin</h1>
          </div>
          <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); navigate({ to: "/login" }); }}>Sign out</Button>
        </div>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-xs font-bold uppercase text-muted-foreground">Total posts</div>
            <div className="mt-1 text-3xl font-extrabold text-primary">{status.data?.postCount ?? "–"}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-xs font-bold uppercase text-muted-foreground">Rotation</div>
            <div className="mt-2 text-sm">
              Res topic: <strong>{status.data?.state?.residential_topic_index ?? 0}</strong><br />
              Com topic: <strong>{status.data?.state?.commercial_topic_index ?? 0}</strong><br />
              City idx: <strong>{status.data?.state?.city_index ?? 0}</strong>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-xs font-bold uppercase text-muted-foreground">Topics seeded</div>
            <div className="mt-1 text-3xl font-extrabold text-primary">{topics.data?.topics?.length ?? "–"}</div>
          </div>
        </section>

        <section className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="text-xl font-bold text-primary">Manual generation</h2>
          <p className="mt-1 text-sm text-muted-foreground">Triggers the same pipeline that runs on schedule. Creates 1 residential + 1 commercial post per click.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={() => genMut.mutate("general")} disabled={genMut.isPending}>Generate general now</Button>
            <Button variant="secondary" onClick={() => genMut.mutate("city")} disabled={genMut.isPending}>Generate city now</Button>
          </div>
          {genMut.isPending && <p className="mt-3 text-sm text-muted-foreground">Generating... this takes ~30 seconds.</p>}
          {genMut.data && (
            <pre className="mt-4 max-h-60 overflow-auto rounded bg-muted p-3 text-xs">{JSON.stringify(genMut.data, null, 2)}</pre>
          )}
          {genMut.error && <p className="mt-3 text-sm text-destructive">{String((genMut.error as any).message)}</p>}
        </section>

        <section className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="text-xl font-bold text-primary">Recent posts</h2>
          <div className="mt-4 divide-y divide-border">
            {(posts.data?.posts ?? []).map((p: any) => (
              <div key={p.id} className="flex items-center justify-between py-3">
                <div>
                  <Link to="/expert-tips/$citySlug/$slug" params={{ citySlug: p.city_slug, slug: p.slug }} className="font-semibold text-primary hover:text-accent">
                    {p.title}
                  </Link>
                  <div className="text-xs text-muted-foreground">{p.audience} · {p.city_slug} · {new Date(p.published_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {posts.data?.posts?.length === 0 && <p className="py-4 text-sm text-muted-foreground">No posts yet. Trigger one above.</p>}
          </div>
        </section>

        <section className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="text-xl font-bold text-primary">Recent runs</h2>
          <div className="mt-4 max-h-80 overflow-auto text-xs font-mono">
            {(status.data?.logs ?? []).map((l: any) => (
              <div key={l.id} className={`py-1 ${l.status === "error" ? "text-destructive" : "text-muted-foreground"}`}>
                [{new Date(l.created_at).toLocaleString()}] {l.slot}/{l.audience} · {l.status} {l.error ? `· ${l.error}` : ""} ({l.duration_ms}ms)
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
